// src/views/Settings/SettingsShell.jsx
//
// El contenedor de la configuración. Es una superficie propia, no un destino
// del rail: se entra, se navega dentro y se sale volviendo a donde estabas.
// La app ya trataba así a /user/:section y /organizations —ambas viven fuera de
// AppShell y no pintan el rail—, así que esto no inventa un modo nuevo, le da
// forma al que ya existía.
//
// El sub-nav muestra los **tres alcances juntos**, cada uno como su propio
// grupo. Las rutas siguen separadas —la URL dice de quién es lo que miras—, pero
// la navegación no: con un grupo por pantalla, el alcance de cuenta se quedaba
// sin ninguna puerta desde el menú. Además el agrupamiento enseña la distinción
// al llegar, en vez de exigir que se adivine desde el menú del avatar.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * @param {Array}    props.groups  [{ key, label, sections: [{ id, i18nKey, label, Icon, path }] }]
 * @param {string}   props.active  path de la sección activa
 */
export default function SettingsShell({ groups = [], active, children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // En móvil el sub-nav no cabe entero: dos grupos con sus encabezados en una
  // tira que se desplaza de lado son siete botones de 90px, ninguno a la vista
  // del todo. Así que se parte en dos por su propia costura —que ya existía—:
  // el grupo al que perteneces ahora se pinta como pestañas arriba, y el otro
  // como una lista al final del contenido. Cada alcance conserva su nombre y
  // ninguno se queda sin puerta, que era el riesgo de quedarse con uno solo.
  const activeGroup =
    groups.find((g) => g.sections.some((sec) => sec.path === active)) || groups[0];
  const otherGroups = groups.filter((g) => g !== activeGroup);

  // Salir va siempre a Home.
  //
  // Antes recordaba de dónde habías entrado y volvía ahí, pero eso hacía que el
  // mismo botón aterrizara en un sitio distinto cada vez —y entrando desde Zeno,
  // que es la ruta por defecto de la app, "Volver" te devolvía al chat—. Un
  // destino fijo y conocido vale más aquí que uno inteligente: configuración es
  // una superficie de la que se sale, no un paso intermedio.
  const handleExit = () => navigate('/home');

  return (
    <section className="min-h-screen bg-black">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col md:flex-row">
        <aside className="shrink-0 p-4 md:w-64 md:border-r md:border-white/10 md:p-6">
          {/* La flecha y el título comparten fila en móvil: son la misma cosa
              —dónde estás y cómo salir— y apiladas se llevaban 90px de alto
              antes de que empiece nada. */}
          <div className="mb-4 flex items-center gap-3 md:mb-0 md:block">
            <button
              onClick={handleExit}
              aria-label={t("settingsNav.back", "Back")}
              className="flex shrink-0 items-center gap-2 rounded-lg text-sm text-gray-400 transition-all duration-300 hover:translate-x-[-2px] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70 md:mb-6"
            >
              <ArrowLeft className="h-6 w-6 md:h-4 md:w-4" />
              <span className="hidden md:inline">{t("settingsNav.back", "Back")}</span>
            </button>

            <h2 className="text-2xl font-bold text-white md:mb-5 md:text-xl">
              {t("settingsNav.navLabel", "Settings")}
            </h2>
          </div>

          {/* Móvil: sólo el grupo activo, como pestañas de pulgar. */}
          <nav
            aria-label={activeGroup?.label}
            className="flex rounded-2xl border border-white/10 bg-white/[0.03] p-1 md:hidden"
          >
            {(activeGroup?.sections || []).map((section) => {
              const Icon = section.Icon;
              const isActive = section.path === active;
              return (
                <button
                  key={`m-${section.id}`}
                  onClick={() => navigate(section.path)}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 text-sm font-medium transition",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
                    isActive
                      ? "border-b-2 border-violet-500 bg-white/[0.07] text-white"
                      : "border-b-2 border-transparent text-gray-400"
                  )}
                >
                  {/* El icono se cae en las pantallas más estrechas. Medido a
                      360 y 390px, con él la pestaña deja 70-78px de texto y
                      "Preferences" pide 85: recortar la palabra que identifica
                      la pestaña para conservar su adorno es al revés. */}
                  {Icon ? <Icon className="hidden h-4 w-4 shrink-0 sm:block" /> : null}
                  <span className="truncate">{t(section.i18nKey, section.label)}</span>
                </button>
              );
            })}
          </nav>

          <nav
            aria-label={t("settingsNav.navLabel", "Settings")}
            className="hidden md:flex md:flex-col md:gap-5"
          >
            {groups.map((group) => (
              <div key={group.key} className="min-w-0">
                {/* El alcance se nombra en el encabezado del grupo, no en cada
                    etiqueta: ahí puede ser largo porque no se lee en cada click. */}
                <div
                  className="mb-1.5 truncate px-3 text-[11px] uppercase tracking-wide text-gray-400"
                  title={group.label}
                >
                  {group.label}
                </div>
                <div className="flex gap-1 md:flex-col">
                  {group.sections.map((section) => {
                    const Icon = section.Icon;
                    const isActive = section.path === active;
                    return (
                      <button
                        key={`${group.key}-${section.id}`}
                        onClick={() => navigate(section.path)}
                        aria-current={isActive ? "page" : undefined}
                        className={cn(
                          "flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
                          isActive
                            ? "bg-white/10 text-white"
                            : "text-gray-400 hover:bg-white/5 hover:text-white"
                        )}
                      >
                        {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
                        <span className="truncate">{t(section.i18nKey, section.label)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Columna flexible con `min-h-0`: las secciones normales fluyen, y las
            que necesitan toda la altura —Projects, que se desplaza por dentro—
            pueden pedir `flex-1` sin calcular píxeles a mano. */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col p-4 md:p-8">
          {children}

          {/* El resto de alcances, al final y sólo en móvil: arriba ya está el
              grupo en el que estás, y meter los dos ahí obliga a elegir entre
              apretarlos o esconder uno. Al final del contenido son una lista de
              verdad —icono, nombre y flecha— en vez de siete pastillas. */}
          {otherGroups.length > 0 && (
            <div className="mt-8 space-y-4 md:hidden">
              {otherGroups.map((group) => (
                <nav
                  key={`list-${group.key}`}
                  aria-label={group.label}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <h3 className="mb-1 text-base font-semibold text-white">{group.label}</h3>
                  <div className="divide-y divide-white/5">
                    {group.sections.map((section) => {
                      const Icon = section.Icon;
                      return (
                        <button
                          key={`list-${group.key}-${section.id}`}
                          onClick={() => navigate(section.path)}
                          className={cn(
                            "flex w-full items-center gap-3 py-3.5 text-left text-[15px] text-gray-200 transition",
                            "active:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
                          )}
                        >
                          {Icon ? <Icon className="h-5 w-5 shrink-0 text-gray-400" /> : null}
                          <span className="min-w-0 flex-1 truncate">
                            {t(section.i18nKey, section.label)}
                          </span>
                          <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
                        </button>
                      );
                    })}
                  </div>
                </nav>
              ))}
            </div>
          )}
        </main>
      </div>
    </section>
  );
}
